import classNames from 'classnames';
import * as React from 'react';
import { IconContext } from './icon_context.js';
/** Exported for tests *only* */
export function getSizeInPx(size, height, width) {
    if (height !== undefined || width !== undefined) {
        // The height and width props have historically overridden the t-shirt sizing `size` prop
        // so we return based on that data
        if (typeof height === 'number') {
            return height;
        }
        if (typeof width === 'number') {
            return width;
        }
        // We know width or height is a string, make sure it is in `px` format
        if (/px$/.test(height || width || '')) {
            // We have `px` in the string, so we can just parse the number
            const sizeNumber = (height || width || '0').match(/\d+/);
            const lastItem = sizeNumber ? sizeNumber.pop() : undefined;
            if (lastItem) {
                const integerSizeNumber = parseInt(lastItem, 10);
                if (!isNaN(integerSizeNumber)) {
                    return parseInt(lastItem, 10);
                }
            }
        }
        else if (!isNaN(height || width)) {
            // We have a number as a string
            return parseInt(height || width || '', 10);
        }
    }
    // Standard t-shirt sizing
    switch (size) {
        case 'small':
            return 16;
        case 'large':
            return 32;
    }
    return 24; // default: medium
}
/**
 * It's the magic number generator!
 *
 * These values were chosen by adjusting the negative margin on `UIIcon` until most icons were either
 * tight cropped, or left with a few pixels of whitespace. This was initially done manually and with eye balls;
 * now it uses fancy math.
 */
function getNegatedMargin(size) {
    // 16 maps to -2
    // 24 maps to -4
    // 32 maps to -6
    // Luckily it is just a linear equation
    // y = -0.25x + 2
    return Math.floor(size / -4) + 2;
}
/**
 * UIIcon represents icons that should be used for interactive parts through out the application.
 *
 * See
 * [drl/dig/library/components/icons](https://dbx.link/dig/library/components/icons)
 * for more details.
 */
export const UIIcon = ({ className, 
// `src` is aliased as `IconComponent` for usage in JSX
src: IconComponent, size: sizeProp = 'medium', isOpticallyAligned: isOpticallyAlignedProp = false, height: heightProp, width: widthProp, ...rest }) => {
    // Size is prioritized to come from context over the prop value
    const { UIIconOverrides } = React.useContext(IconContext);
    const _size = UIIconOverrides && UIIconOverrides.size ? UIIconOverrides.size : sizeProp;
    const size = _size === 'standard' ? 'medium' : _size === 12 ? 'small' : _size === 20 ? 'medium' : _size;
    const opticalAlignment = UIIconOverrides && typeof UIIconOverrides.isOpticallyAligned !== 'undefined'
        ? UIIconOverrides.isOpticallyAligned
        : isOpticallyAlignedProp;
    const cls = classNames('dig-UIIcon', {
        'dig-UIIcon--small': size === 'small',
        'dig-UIIcon--standard': size === 'medium',
        'dig-UIIcon--is-optically-aligned': opticalAlignment,
    }, className);
    const isPresentation = !rest['aria-label'] && !rest['aria-labelledby'];
    const sizeUnit = getSizeInPx(size, heightProp, widthProp);
    const negatedMargin = {
        marginLeft: getNegatedMargin(sizeUnit),
        marginRight: getNegatedMargin(sizeUnit),
    };
    if (!opticalAlignment) {
        return (React.createElement(IconComponent, { className: cls, width: sizeUnit, height: sizeUnit, role: isPresentation ? 'presentation' : undefined, focusable: "false", ...rest }));
    }
    return (
    // `cls` is moved to the wrapping `span` here because we want this to operate as if the `span`
    // was the `IconComponent` itself.
    // inline-flex and lineHeight:0 are used to remove the whitespace below the icon that would appear
    // due to `inline-flex` inheriting line-height from the parent.
    React.createElement("span", { className: cls, style: { display: 'inline-flex', lineHeight: 0 } },
        React.createElement(IconComponent, { width: sizeUnit, height: sizeUnit, role: isPresentation ? 'presentation' : undefined, focusable: "false", style: negatedMargin, ...rest })));
};
UIIcon.displayName = 'UIIcon';
